import React, { Component, createRef } from "react";
import classnames from "classnames";
import PropTypes from "prop-types";
import Button from "../../Button/Button";
import { IconAdd16 } from "../../Icons/Icons";
import "./file-uploader.scss";
import Table from "../../Table/Table";
import TableBody from "../../Table/TableBody";
import TableRow from "../../Table/TableRow";
import TableCell from "../../Table/TableCell";
import Alert from "../../Alert/Alert";

const fileListToArray = list => {
  const result = [];
  if (!list) return result;
  for (let i = 0; i < list.length; i += 1) {
    result.push({ file: list.item(i) });
  }
  return result;
};

class FileUploader extends Component {
  constructor(props) {
    super(props);
    this.state = { highlight: false, inputKey: Date.now() };
    this.fileInputRef = createRef();

    this.openFileDialog = this.openFileDialog.bind(this);
    this.onFilesAdded = this.onFilesAdded.bind(this);
    this.onDragOver = this.onDragOver.bind(this);
    this.onDragLeave = this.onDragLeave.bind(this);
    this.onDrop = this.onDrop.bind(this);
    this.onFileRemove = this.onFileRemove.bind(this);
  }

  onFileRemove(file) {
    const { onRemove } = this.props;
    this.setState({ inputKey: Date.now() });
    onRemove(file);
  }

  onFilesAdded(e) {
    const { disabled, onAdd } = this.props;
    if (disabled) return;
    const { files } = e.target;
    const array = fileListToArray(files);
    onAdd(array);
  }

  onDragOver(e) {
    e.preventDefault();
    const { disabled } = this.props;
    if (disabled) return;

    this.setState({ highlight: true });
  }

  onDragLeave() {
    this.setState({ highlight: false });
  }

  onDrop(e) {
    e.preventDefault();
    const { disabled, onAdd } = this.props;
    if (disabled) return;

    const { files } = e.dataTransfer;
    const array = fileListToArray(files);
    onAdd(array);
    this.setState({ highlight: false });
  }

  openFileDialog() {
    this.fileInputRef.current.click();
  }

  render() {
    const {
      id,
      multiple,
      disabled,
      addButtonText,
      className,
      files,
      accept,
      removeText,
      filePendingText,
      fileSuccessText,
      fileErroredText,
      onRemove,
      removeable,
    } = this.props;
    const { highlight, inputKey } = this.state;
    const classes = classnames(
      "file-uploader",
      { "file-uploader--highlight": highlight },
      className
    );

    const hasStatuses = files.some(file => !!file.status);

    const getStatusCol = file => {
      if (!hasStatuses) return null;
      switch (file.status) {
        case "error":
          return (
            <TableCell>
              <Alert className="alert--inline" type="error">
                {fileErroredText}
              </Alert>
            </TableCell>
          );
        case "pending":
          return (
            <TableCell>
              <Alert className="alert--inline" type="info">
                {filePendingText}
              </Alert>
            </TableCell>
          );
        case "success":
          return (
            <TableCell>
              <Alert className="alert--inline" type="success">
                {fileSuccessText}
              </Alert>
            </TableCell>
          );
        default:
          return <TableCell />;
      }
    };

    const erroredFile = (file, key) => {
      return (
        <TableRow key={key}>
          {getStatusCol(file)}
          <TableCell label="FileName" className="u-error">
            {file.errorMessage}
          </TableCell>
          {onRemove && <TableCell />}
        </TableRow>
      );
    };

    const addedFile = (file, key) => {
      return (
        <TableRow key={key}>
          {getStatusCol(file)}
          <TableCell>{file.file.name}</TableCell>
          {onRemove && removeable && (
            <TableCell className="file-uploader__remove-cell">
              <Button
                id={`removeBtn_${key}`}
                disabled={disabled}
                variant="link"
                text={removeText}
                onClick={() => this.onFileRemove(file)}
              />
            </TableCell>
          )}
        </TableRow>
      );
    };

    return (
      <div
        className={classes}
        onDragOver={this.onDragOver}
        onDragLeave={this.onDragLeave}
        onDrop={this.onDrop}
      >
        {files && files.length > 0 && (
          <Table className="file-uploader__table">
            <TableBody>
              {files.map((file, idx) =>
                file.errorMessage ? erroredFile(file, idx) : addedFile(file, idx)
              )}
            </TableBody>
          </Table>
        )}

        <input
          id={id}
          ref={this.fileInputRef}
          key={inputKey}
          className="file-uploader__input"
          type="file"
          multiple={multiple}
          disabled={disabled}
          files={files}
          accept={accept}
          onChange={this.onFilesAdded}
        />
        <Button
          variant="outlined"
          text={addButtonText}
          icon={IconAdd16}
          className="file-uploader__button"
          onClick={this.openFileDialog}
          disabled={disabled}
        />
      </div>
    );
  }
}

FileUploader.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  multiple: PropTypes.bool,
  removeable: PropTypes.bool,
  disabled: PropTypes.bool,
  addButtonText: PropTypes.string.isRequired,
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func,
  removeText: PropTypes.string,
  files: PropTypes.arrayOf(PropTypes.shape({})),
  accept: PropTypes.string,

  fileSuccessText: PropTypes.string,
  filePendingText: PropTypes.string,
  fileErroredText: PropTypes.string,
};

FileUploader.defaultProps = {
  removeText: "Remove",
  removeable: true,
  files: [],
  fileSuccessText: "Uploaded",
  fileErroredText: "Failed",
  filePendingText: "Pending",
};

export default FileUploader;
